import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
  MouseEvent,
  useMemo,
  JSX,
} from 'react';

import { useMutation } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ContextMenu, Preloader, useContextMenu } from '@smartshell/ui-kit';

import { LayoutContext } from 'components/Layout';
import { useWindowResize } from 'hooks';
import {
  GET_SHORTCUTS,
  IShortcut,
  IUpdateAllShortcutsRequest,
  IUpdateAllShortcutsResponse,
  IUpdateShortcutRequest,
  IUpdateShortcutResponse,
  IUpdateSortRequest,
  UPDATE_ALL_SHORTCUTS,
  UPDATE_SHORTCUT,
  UPDATE_SORT,
} from 'schemas';

import { ApplicationCard } from '../../ApplicationCard';
import { SortableCardItem } from '../../SortableCardItem';
import { useContextMenuItems } from '../../useContextMenuItems';
import { getIndex } from '../../utils';
import Group from '../Group';
import EditGroupModal from '../Group/components/GroupModal/EditGroupModal';
import ApplicationTop from '../Top';
import { ShortcutsProps } from './interfaces';

const Shortcuts: FC<ShortcutsProps> = ({
  OpenTabRadio,
  shortcuts,
  selectedApp,
  setSelectedApp,
  refetch,
  loadingData,
  groups,
  size = 's',
  placeholder,
  defaultGroupTitle,
  appType,
  linkTitle,
  groupPlaceholder,
}) => {
  const layoutElement = useContext(LayoutContext);
  const sizeWindow = useWindowResize();
  const [selectedItem, setSelectedItem] = useState<IShortcut | null>(null);
  const [timer, setTimer] = useState<null | NodeJS.Timeout>(null);

  const [updateSort, { loading: sortChanging }] = useMutation<
    boolean,
    IUpdateSortRequest
  >(UPDATE_SORT, {
    refetchQueries: [GET_SHORTCUTS],
    awaitRefetchQueries: true,
  });

  const [updateShortcut, { loading: updateShortcutLoading }] = useMutation<
    IUpdateShortcutResponse,
    IUpdateShortcutRequest
  >(UPDATE_SHORTCUT, {
    refetchQueries: [GET_SHORTCUTS],
    awaitRefetchQueries: true,
  });

  const [updateAllShortcut, { loading: updateAllShortcutLoading }] =
    useMutation<IUpdateAllShortcutsResponse, IUpdateAllShortcutsRequest>(
      UPDATE_ALL_SHORTCUTS,
      {
        refetchQueries: [GET_SHORTCUTS],
        awaitRefetchQueries: true,
      }
    );

  const contextMenuItems = useContextMenuItems(
    refetch,
    selectedItem,
    groups,
    appType,
    defaultGroupTitle
  );

  const { onContextMenu, contextMenuData, closeContextMenu, cursorPosition } =
    useContextMenu<IShortcut>();

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const [items, setItems] = useState<IShortcut[]>(shortcuts);

  const [selectedShortcut, setSelectedShortcut] = useState<IShortcut | null>(
    null
  );

  const [groupsClosed, setGroupsClosed] = useState<Set<number>>(new Set());

  useEffect(() => {
    setItems(shortcuts);
  }, [shortcuts]);

  const [isOpenModel, setIsOpenModal] = useState<boolean>(false);

  const onContextMenuHandled = useCallback(
    (item: IShortcut) => (event: MouseEvent<HTMLDivElement>) => {
      setSelectedItem(item);
      onContextMenu(event, item);
    },
    [items, onContextMenu]
  );

  const onDragOver = useCallback(
    ({ over, active }: DragOverEvent): void => {
      if (!over || !active) return;

      const activeId = active.id;
      const overId = over.id;

      if (activeId === overId) return;

      const isActiveAApp = active.data.current?.type === 'Shortcut';
      const isOverAApp = over.data.current?.type === 'Shortcut';
      const isOverAGroup = over.data.current?.type === 'Group';

      if (!isActiveAApp) return;

      if (isActiveAApp && isOverAApp) {
        const activeIndex = getIndex(Number(activeId), items);
        const overIndex = getIndex(Number(overId), items);

        setItems((prevItems) => {
          if (
            prevItems[activeIndex].group.id !== prevItems[overIndex].group.id
          ) {
            prevItems[activeIndex].group = { ...prevItems[overIndex].group };

            return arrayMove(prevItems, activeIndex, overIndex - 1);
          }

          return arrayMove(prevItems, activeIndex, overIndex);
        });
      }

      if (isActiveAApp && isOverAGroup) {
        const activeIndex = getIndex(Number(activeId), items);
        const activeApp = shortcuts.find(({ id }) => activeId === id);
        const findGroup = groups.find(({ id }) => id === overId);

        if (activeApp && findGroup) {
          if (activeApp.group.id === overId) return;

          setItems((prevItems) => {
            prevItems[activeIndex].group = {
              ...findGroup,
            };

            return arrayMove(prevItems, activeIndex, activeIndex);
          });
        }
      }
    },
    [selectedShortcut, items]
  );

  const onDragEnd = useCallback(
    ({ over }: DragEndEvent) => {
      if (over) {
        const changedDataSort: IShortcut[] = [];
        let changedDataGroup: IShortcut = {} as IShortcut;
        const groupsMap: Record<string, IShortcut[]> = {};

        items.forEach((item) => {
          if (!groupsMap[item.group.id]) {
            groupsMap[item.group.id] = [];
          }

          groupsMap[item.group.id].push({ ...item });
        });

        for (const key in groupsMap) {
          groupsMap[key].forEach((item, index) => {
            if (item.group.id !== item.group_id) {
              changedDataGroup = {
                ...item,
                group_id: item.group.id,
                sort: index,
              };
            } else if (item.sort !== index) {
              changedDataSort.push({
                ...item,
                sort: index,
              });
            }
          });
        }

        if (
          Object.keys(changedDataGroup).length !== 0 &&
          changedDataSort.length !== 0
        ) {
          updateAllShortcut({
            variables: {
              updateShortcutId: changedDataGroup.id,
              updateShortcutInput: {
                group_id: changedDataGroup.group_id,
                sort: changedDataGroup.sort,
              },
              updateShortcutSortInput: changedDataSort.map((item) => ({
                sort: item.sort,
                id: item.id,
              })),
            },
          });
        } else if (changedDataSort.length !== 0) {
          updateSort({
            variables: {
              input: changedDataSort.map((item) => ({
                sort: item.sort,
                id: item.id,
              })),
            },
          });
        } else if (Object.keys(changedDataGroup).length !== 0) {
          updateShortcut({
            variables: {
              id: changedDataGroup.id,
              input: {
                group_id: changedDataGroup.group_id,
                sort: changedDataGroup.sort,
              },
            },
          });
        }
      }
    },
    [items]
  );

  const closeContextMenuHandled = (): void => {
    setSelectedItem(null);
    closeContextMenu();
  };

  const onDragStart = ({ active }: DragStartEvent): void => {
    closeContextMenuHandled();
    setSelectedShortcut(
      shortcuts.find(({ id }) => id === Number(active.id)) || null
    );
  };

  const handleDragOverThrottled = (event: DragOverEvent): void => {
    if (timer) {
      clearTimeout(timer);
    }

    setTimer(setTimeout(() => onDragOver(event), 0));
  };

  const toggleHandle = useCallback(
    (id: number) => () => {
      const newSet: Set<number> = new Set(groupsClosed);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      setGroupsClosed(newSet);
    },
    [groupsClosed]
  );

  const closeModal = useCallback(() => setIsOpenModal(false), [isOpenModel]);

  const openModal = useCallback(() => setIsOpenModal(true), [isOpenModel]);

  const closeAllGroupsHandler = useCallback(() => {
    const newSet = new Set(groups.map(({ id }) => id));

    setGroupsClosed(newSet);
  }, [groups, groupsClosed]);

  const disabledGroup = useMemo<Set<number>>(
    () => new Set<number>(shortcuts.map(({ group_id }) => group_id)),
    [shortcuts]
  );

  const renderGroups = (): JSX.Element => (
    <>
      {groups.map((group) => (
        <Group
          key={group.id}
          items={items.filter((item) => item.group.id === group.id)}
          selectedApp={selectedApp}
          setSelectedApp={setSelectedApp}
          disabled={
            sortChanging || updateAllShortcutLoading || updateShortcutLoading
          }
          onContextMenu={onContextMenuHandled}
          size={size}
          placeholder={
            group.groupType === 'DEFAULT' ? placeholder : groupPlaceholder
          }
          title={
            group.groupType === 'DEFAULT' ? defaultGroupTitle : group.title
          }
          isOpen={!groupsClosed.has(group.id)}
          setIsOpen={toggleHandle(group.id)}
          id={group.id}
        />
      ))}

      <DragOverlay>
        {selectedShortcut && (
          <SortableCardItem id={selectedShortcut.id} overlay size={size}>
            <ApplicationCard
              title={selectedShortcut.title}
              isActive={selectedShortcut.id === selectedApp?.id}
              size={size}
              imagePath={selectedShortcut.main_picture}
              age={
                selectedShortcut.only_for_registered
                  ? selectedShortcut.age_rating
                  : null
              }
            />
          </SortableCardItem>
        )}
      </DragOverlay>
    </>
  );
  return (
    <>
      <ApplicationTop
        OpenTabRadio={OpenTabRadio}
        link={`/applications/manage?type=${appType}`}
        linkTitle={linkTitle}
        openModal={openModal}
        closeAllGroups={closeAllGroupsHandler}
        disabledCloseButton={groups.length < 2}
      />
      <Preloader loading={loadingData}>
        {contextMenuData && cursorPosition && (
          <ContextMenu
            layoutElement={layoutElement}
            data={contextMenuData}
            items={contextMenuItems}
            closeContextMenu={closeContextMenuHandled}
            position={cursorPosition}
          />
        )}
        {sizeWindow.width > 960 ? (
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={handleDragOverThrottled}
          >
            {renderGroups()}
          </DndContext>
        ) : (
          renderGroups()
        )}
      </Preloader>
      {isOpenModel && (
        <EditGroupModal
          groups={groups.filter((group) => group.groupType !== 'DEFAULT')}
          closeModal={closeModal}
          appType={appType}
          disabledGroup={disabledGroup}
        />
      )}
    </>
  );
};

export default Shortcuts;
